import React from 'react';
import { Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';

export type Option = {
  id: string;
  label: string;
};

interface SearchableTagInputProps {
  label: string;
  options: Option[];
  selectedOptions: Option[];
  onChange: (options: Option[]) => void;
  maxSelections?: number;
  placeholder?: string;
  className?: string;
}

export function SearchableTagInput({
  label,
  options,
  selectedOptions,
  onChange,
  maxSelections = 10,
  placeholder = 'Search',
  className
}: SearchableTagInputProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filteredOptions = React.useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  const toggleOption = (option: Option) => {
    const isSelected = selectedOptions.some((item) => item.id === option.id);

    if (isSelected) {
      onChange(selectedOptions.filter((item) => item.id !== option.id));
    } else {
      if (selectedOptions.length < maxSelections) {
        onChange([...selectedOptions, option]);
      }
    }
  };

  const handleClickOutside = React.useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div
      className={cn('w-full space-y-1.5 font-grotesk', className)}
      ref={containerRef}
    >
      <label className='text-base font-medium text-[#111014]'>{label}</label>

      <div className='relative h-12 rounded-md border bg-[#f9fafb]'>
        <button
          type='button'
          onClick={() => setIsOpen(!isOpen)}
          className='flex w-full items-center justify-between rounded-md border border-input px-3 py-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          aria-haspopup='listbox'
          aria-expanded={isOpen}
        >
          <span className='block truncate'>
            {selectedOptions.length > 0
              ? `${selectedOptions.length} selected`
              : 'Select options'}
          </span>
          {isOpen ? (
            <ChevronUp className='h-4 w-4 opacity-70' />
          ) : (
            <ChevronDown className='h-4 w-4 opacity-70' />
          )}
        </button>

        {isOpen && (
          <div className='absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg'>
            <div className='relative p-2'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
              <Input
                ref={inputRef}
                type='text'
                placeholder={placeholder}
                className='w-full rounded-md border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className='max-h-60 overflow-auto p-1'>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = selectedOptions.some(
                    (item) => item.id === option.id
                  );
                  return (
                    <div
                      key={option.id}
                      onClick={() => toggleOption(option)}
                      className={cn(
                        'flex cursor-pointer items-center rounded-md px-2 py-2 text-sm',
                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-100'
                      )}
                    >
                      <div className='mr-2 flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white'>
                        {isSelected && (
                          <Check className='h-3.5 w-3.5 text-blue-600' />
                        )}
                      </div>
                      <span>{option.label}</span>
                    </div>
                  );
                })
              ) : (
                <div className='px-2 py-2 text-sm text-gray-500'>
                  No results found
                </div>
              )}
            </div>

            {maxSelections && (
              <div className='border-t border-gray-200 px-2 py-2 text-xs text-gray-500'>
                You can select up to {maxSelections} options
              </div>
            )}
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className='flex flex-wrap gap-2 pt-2'>
          {selectedOptions.map((option) => (
            <div
              key={option.id}
              className='flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm text-blue-800'
            >
              <span>{option.label}</span>
              <button
                type='button'
                onClick={() => toggleOption(option)}
                className='ml-1 rounded-full p-0.5 hover:bg-blue-200'
              >
                <X className='h-3 w-3' />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
